import { isMobile } from 'react-device-detect';
import { useState } from 'react';
import { useAppDispatch } from '../../../../providers';
import {
  Drawer,
  DrawerContent,
  Button,
  TextInput,
  Icon,
  Modal,
  ModalRoot,
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '@pella/sash-framework';
import { SearchInput, ConfirmDeleteModal } from '../../../atoms';

import * as classes from './SelectionListValue.module.css';

import { SELECTION_LIST_SELECTED_ID } from '../../../../providers/AppProvider/types';
import {
  useUpdateSelectionListValue,
  useDeleteSelectionListValue,
  useCreateSelectionListValue,
} from '../../../../hooks';

/**
 * Component for the SelectionListValues Drawer.
 * @param {Local.SelectionListValuesProps} props - Component props
 */
export const SelectionListValuesDrawer = (props) => {
  const { data } = props;
  const dispatch = useAppDispatch();

  const { mutate: createSelectionListValue } = useCreateSelectionListValue();

  const [input, setInput] = useState('');

  const [searchValue, setSearchValue] = useState('');

  // Create a local copy of values to avoid mutating props

  const [localValues, setLocalValues] = useState({});
  const updateSelectionListValue = useUpdateSelectionListValue();
  const deleteSelectionListValue = useDeleteSelectionListValue();
  const [confirmData, setConfirmData] = useState(/** @type {Local.SelectionListValue | undefined} */ (undefined));

  const [handleUnsavedChangesOpen, setHandleUnsavedChangesOpen] = useState(false);

  const filteredData =
    data?.values?.filter((item) => item.description.toLowerCase().includes(searchValue.toLowerCase())) || [];

  /** * Handles the drawer close action. */
  const handleClose = () => {
    if (localValues && Object.keys(localValues).length > 0) {
      setHandleUnsavedChangesOpen(true);
    } else {
      setInput('');
      dispatch({ type: SELECTION_LIST_SELECTED_ID, payload: '' });
    }
  };

  const handleDiscardChanges = () => {
    setHandleUnsavedChangesOpen(false);
    setLocalValues({});
    setInput('');
    dispatch({ type: SELECTION_LIST_SELECTED_ID, payload: '' });
  };

  /**
   * handles edit click and save
   * @param {string} valueId
   */
  const handleEditClick = (valueId) => {
    const editingValue = filteredData.find((v) => v.id === valueId); // Ensure the value exists before allowing edit

    /* v8 ignore next */
    if (editingValue) {
      // Use local value if it exists, otherwise fall back to original value
      const currentValue = localValues[valueId] ?? filteredData.find((v) => v.id === valueId)?.description;
      updateSelectionListValue.mutate(
        {
          id: valueId,
          input: {
            description: currentValue,
          },
        },
        {
          onSuccess: () => {
            // Remove the valueId from localValues once the update is complete
            setLocalValues((prev) => {
              const updated = { ...prev };
              delete updated[valueId];
              return updated;
            });
          },
        },
      );
    }
  };

  /**
   * updates the value in the text input
   * @param {string} valueId
   * @param {string} newValue
   */
  const handleEditInput = (valueId, newValue) => {
    // Update local state instead of mutating props
    setLocalValues((prev) => ({
      ...prev,
      [valueId]: newValue,
    }));
  };

  /**
   * Handle delete action
   * @param {Local.SelectionListValue} value - The value being deleted
   */
  const handleOnDelete = (value) => setConfirmData(value);

  /** Handle confirm delete action */
  const handleConfirmDelete = () => {
    // I want to ts-ignore this. It will never happen that confirmData is undefined here, you cannot trigger this on a value that does not exist
    /* v8 ignore next */
    if (confirmData?.id) {
      const valueId = confirmData.id;
      deleteSelectionListValue.mutate(confirmData.id, {
        onSuccess: () => {
          // Remove the valueId from localValues once the update is complete
          setLocalValues((prev) => {
            const updated = { ...prev };
            delete updated[valueId];
            return updated;
          });
        },
      });
    }
    setConfirmData(undefined);
  };

  const handleCreateSelectionListValue = () => {
    /* v8 ignore next */
    if (data?.id) {
      createSelectionListValue(
        {
          listId: data.id,
          description: input,
        },
        {
          onSuccess: () => {
            setInput('');
          },
        },
      );
    }
  };

  return (
    <Drawer direction={isMobile ? 'bottom' : 'right'} open={Boolean(data?.id)} onOpenChange={handleClose}>
      <DrawerContent titleText='Selection List Value Assignment' showClose={false}>
        <div className={classes.content}>
          <div>{data?.description}</div>
          <div className={classes.selectContainer}>
            <TextInput
              className={classes.select}
              id='selection-list-value-input'
              data-testid='selection-list-value-input'
              labelText='Enter Value'
              onChange={(e) => setInput(e.target.value)}
              value={input}
              maxLength={50}
              required
            />
            <Button
              data-testid={`selection-list-value-assign-${data?.id}`}
              className={classes.assignButton}
              color='primary'
              disabled={!input.trim()}
              onClick={handleCreateSelectionListValue}
            >
              Add Value
            </Button>
          </div>
        </div>
        <SearchInput id='selection-list-values-search-input' searchValue={searchValue} onChange={setSearchValue} />
        <div>
          {Boolean(filteredData?.length) &&
            filteredData.map((value) => {
              // Use local value if it exists, otherwise use the original prop value
              const displayValue = localValues[value.id] ?? value.description;

              return (
                <div key={value.id} className={classes.valueItem}>
                  <TextInput
                    id={`selection-list-value-${value.description}`}
                    labelText=''
                    value={displayValue}
                    maxLength={50}
                    onChange={(e) => handleEditInput(value.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleEditClick(value.id);
                      }
                    }}
                    iconRight={
                      displayValue !== value.description && (
                        <Button
                          color='tertiary'
                          aria-label={`Save ${displayValue}`}
                          onClick={() => handleEditClick(value.id)}
                          data-testid={`setup-card-edit-${value.description}`}
                        >
                          <Icon name='check' />
                        </Button>
                      )
                    }
                    aria-label={`Value ${displayValue}`}
                  />
                  <Button
                    data-testid={`setup-card-delete-${value.description}`}
                    color='tertiary'
                    iconLeft='delete'
                    aria-label={`Delete ${displayValue}`}
                    onClick={() => handleOnDelete(value)}
                  />
                </div>
              );
            })}
          <ConfirmDeleteModal
            isOpen={Boolean(confirmData)}
            description={confirmData?.description}
            onConfirm={handleConfirmDelete}
            onCancel={() => setConfirmData(undefined)}
          />
        </div>
        <div className={classes.drawerFooter}>
          <Button
            data-testid={`selection-list-value-done-${data?.id}`}
            className={classes.doneButton}
            color='primary'
            onClick={handleClose}
          >
            CLOSE
          </Button>
        </div>
        <ModalRoot open={handleUnsavedChangesOpen}>
          <Modal showClose={false}>
            <ModalHeader>Unsaved Changes</ModalHeader>
            <ModalContent>
              <p>You have unsaved changes. Are you sure you want to close?</p>
            </ModalContent>
            <ModalFooter className={classes.modalFooter}>
              <Button color='secondary' onClick={() => setHandleUnsavedChangesOpen(false)}>
                Cancel
              </Button>
              <Button color='primary' onClick={handleDiscardChanges}>
                Discard Changes
              </Button>
            </ModalFooter>
          </Modal>
        </ModalRoot>
      </DrawerContent>
    </Drawer>
  );
};
